import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { apiClient } from '../utils/apiClient';
import { useLocation } from 'react-router-dom';
import { API_BASE_URL } from '../constants/api';
import { useAuth } from '../context/AuthContext';
import { ROLE_IDS, hasEffectivePermission } from '../utils/accessControl';
import AppIcon from '../components/AppIcon';
import '../styles/ChangeOrders.css';

const API_BASE = API_BASE_URL;
const STATUS_OPTIONS = [
  { value: 'all', label: 'Tümü' },
  { value: 'draft', label: 'Taslak' },
  { value: 'pending_approval', label: 'Onay Bekliyor' },
  { value: 'approved', label: 'Onaylandı' },
  { value: 'rejected', label: 'Reddedildi' },
  { value: 'cancelled', label: 'İptal' },
];
const STATUS_LABELS = {
  draft: 'Taslak',
  pending_approval: 'Onay Bekliyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  cancelled: 'İptal',
};

const INITIAL_FORM = {
  project_id: '',
  board_node_id: '',
  title: '',
  description: '',
  scope_impact: '',
  schedule_impact_days: 0,
  cost_impact: 0,
  owner_id: '',
  status: 'draft',
};

const flattenBoardOptions = (nodes, level = 0, collector = []) => {
  nodes.forEach((node) => {
    collector.push({
      id: node.id,
      label: `${'  '.repeat(level)}${node.name}`,
    });
    flattenBoardOptions(node.children || [], level + 1, collector);
  });
  return collector;
};

const ChangeOrders = () => {
  const { user, permissions } = useAuth();
  const location = useLocation();
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [projectFilter, setProjectFilter] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formState, setFormState] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [boardTree, setBoardTree] = useState([]);
  const [boardLoading, setBoardLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(0);
  const [focusedChangeOrderId, setFocusedChangeOrderId] = useState(0);
  const [showInfo, setShowInfo] = useState(false);

  const canApprove =
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER].includes(Number(user?.role_id)) &&
    hasEffectivePermission(user, permissions, 'can_manage_change_orders');
  const canManage =
    [ROLE_IDS.ADMIN, ROLE_IDS.PROJECT_MANAGER, ROLE_IDS.SITE_SUPERVISOR].includes(Number(user?.role_id)) &&
    hasEffectivePermission(user, permissions, 'can_manage_change_orders');

  const boardOptions = useMemo(() => flattenBoardOptions(boardTree), [boardTree]);

  const fetchProjects = useCallback(async () => {
    const response = await apiClient.get(`${API_BASE}/projects`);
    setProjects(Array.isArray(response.data) ? response.data : []);
  }, []);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/users/options`);
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setUsers([]);
    }
  }, []);

  const fetchBoardTree = useCallback(async (projectId) => {
    const numericProjectId = Number(projectId || 0);
    if (!numericProjectId) {
      setBoardTree([]);
      return;
    }
    try {
      setBoardLoading(true);
      const response = await apiClient.get(`${API_BASE}/tasks/boards/project/${numericProjectId}/tree`);
      setBoardTree(Array.isArray(response.data?.tree) ? response.data.tree : []);
    } catch (requestError) {
      setBoardTree([]);
    } finally {
      setBoardLoading(false);
    }
  }, []);

  const fetchChangeOrders = useCallback(async () => {
    try {
      setLoading(true);
      const query = new URLSearchParams();
      if (statusFilter !== 'all') query.set('status', statusFilter);
      if (projectFilter !== 'all') query.set('project_id', String(projectFilter));
      const response = await apiClient.get(`${API_BASE}/change-orders?${query.toString()}`);
      setRows(Array.isArray(response.data) ? response.data : []);
      setError('');
    } catch (requestError) {
      setRows([]);
      setError(requestError.response?.data?.message || 'Değişiklik emirleri yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [projectFilter, statusFilter]);

  useEffect(() => {
    fetchProjects();
    fetchUsers();
  }, [fetchProjects, fetchUsers]);

  useEffect(() => {
    fetchChangeOrders();
  }, [fetchChangeOrders]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const focusId = Number(params.get('focusChangeOrder') || 0);
    setFocusedChangeOrderId(focusId > 0 ? focusId : 0);
  }, [location.search]);

  useEffect(() => {
    const sourceProjectId = formState.project_id || (projectFilter !== 'all' ? projectFilter : '');
    if (sourceProjectId) {
      fetchBoardTree(sourceProjectId);
    } else {
      setBoardTree([]);
    }
  }, [fetchBoardTree, formState.project_id, projectFilter]);

  const openCreateForm = () => {
    if (!canManage) return;
      setEditingRow(null);
      setFormState({
        ...INITIAL_FORM,
        project_id: projectFilter !== 'all' ? String(projectFilter) : '',
        owner_id: String(user?.id || ''),
      });
    setShowForm(true);
  };

  const openEditForm = (row) => {
    if (!canManage) return;
    setEditingRow(row);
    setFormState({
      project_id: String(row.project_id || ''),
      board_node_id: row.board_node_id ? String(row.board_node_id) : '',
      title: row.title || '',
      description: row.description || '',
      scope_impact: row.scope_impact || '',
      schedule_impact_days: Number(row.schedule_impact_days || 0),
      cost_impact: Number(row.cost_impact || 0),
      owner_id: row.owner_id ? String(row.owner_id) : '',
      status: row.status || 'draft',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingRow(null);
    setFormState(INITIAL_FORM);
  };

  const saveForm = async () => {
    if (!canManage) return;
    if (!formState.project_id || !String(formState.title || '').trim()) {
      setError('Proje ve başlık zorunludur.');
      return;
    }

    try {
      setSubmitting(true);
      setError('');
      const payload = {
        ...formState,
        project_id: Number(formState.project_id),
        board_node_id: formState.board_node_id ? Number(formState.board_node_id) : null,
        owner_id: formState.owner_id ? Number(formState.owner_id) : null,
        schedule_impact_days: Number(formState.schedule_impact_days || 0),
        cost_impact: Number(formState.cost_impact || 0),
      };
      if (editingRow?.id) {
        await apiClient.put(`${API_BASE}/change-orders/${editingRow.id}`, payload);
      } else {
        await apiClient.post(`${API_BASE}/change-orders`, payload);
      }

      await fetchChangeOrders();
      closeForm();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Kayıt işlemi başarısız.');
    } finally {
      setSubmitting(false);
    }
  };

  const submitForApproval = async (id) => {
    if (!canManage) return;
    try {
      setApprovingId(Number(id));
      await apiClient.post(`${API_BASE}/change-orders/${id}/submit`, {});
      await fetchChangeOrders();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Onaya gönderme işlemi başarısız.');
    } finally {
      setApprovingId(0);
    }
  };

  const approveChangeOrder = async (id, decision) => {
    if (!canApprove) return;
    const approvalNotes = window.prompt(
      decision === 'approved'
        ? 'Onay notu (opsiyonel):'
        : 'Red nedeni (opsiyonel):',
      ''
    );

    try {
      setApprovingId(Number(id));
      await apiClient.post(
        `${API_BASE}/change-orders/${id}/approve`,
        { decision, approval_notes: approvalNotes || '' }
      );
      await fetchChangeOrders();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Onay işlemi başarısız.');
    } finally {
      setApprovingId(0);
    }
  };

  const createRevision = async (row) => {
    if (!canManage) return;
    try {
      setApprovingId(Number(row.id));
      await apiClient.post(
        `${API_BASE}/change-orders/${row.id}/revision`,
        {
          title: `${row.title} (Rev.${Number(row.revision_no || 1) + 1})`,
        },
      );
      await fetchChangeOrders();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Revizyon oluşturulamadı.');
    } finally {
      setApprovingId(0);
    }
  };

  const removeChangeOrder = async (id) => {
    if (!canManage) return;
    if (!window.confirm('Değişiklik emri silinsin mi?')) return;
    try {
      await apiClient.delete(`${API_BASE}/change-orders/${id}`);
      await fetchChangeOrders();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Silme işlemi başarısız.');
    }
  };

  const summary = useMemo(() => {
    const stats = {
      total: rows.length,
      draft: 0,
      pending_approval: 0,
      approved: 0,
      rejected: 0,
      cancelled: 0,
    };
    rows.forEach((row) => {
      const key = String(row.status || 'draft').toLowerCase();
      if (Object.prototype.hasOwnProperty.call(stats, key)) {
        stats[key] += 1;
      }
    });
    return stats;
  }, [rows]);

  return (
    <div className="change-orders-page">
      <div className="change-orders-card">
        <div className="change-orders-head">
          <div>
            <div className="module-title-row">
              <h1>Değişiklik Emri Yönetimi</h1>
              <button
                type="button"
                className={`module-info-btn ${showInfo ? 'active' : ''}`}
                onClick={() => setShowInfo((prev) => !prev)}
                title="Bu modül ne işe yarar?"
              >
                <AppIcon name="info" className="module-info-icon" />
              </button>
            </div>
            {showInfo && (
              <div className="module-info-card">
                <p>
                  Değişiklik Emri, proje başladıktan sonra ortaya çıkan <strong>kapsam, süre veya maliyet</strong>
                  değişikliklerini resmi olarak kaydetmek için kullanılır.
                </p>
                <p>
                  Talep açma, etki analizi, onay süreci ve revizyon geçmişi tek yerde tutulur.
                  Bu yapı, sahadaki değişikliklerin izlenebilir ve denetlenebilir olmasını sağlar.
                </p>
              </div>
            )}
          </div>
          {canManage && (
            <button type="button" className="btn-primary" onClick={openCreateForm}>
              Yeni Değişiklik Emri
            </button>
          )}
        </div>

        <div className="change-orders-summary">
          <div className="summary-box"><span>Toplam</span><strong>{summary.total}</strong></div>
          <div className="summary-box"><span>Taslak</span><strong>{summary.draft}</strong></div>
          <div className="summary-box"><span>Onay Bekliyor</span><strong>{summary.pending_approval}</strong></div>
          <div className="summary-box"><span>Onaylandı</span><strong>{summary.approved}</strong></div>
          <div className="summary-box"><span>Reddedildi</span><strong>{summary.rejected}</strong></div>
        </div>

        <div className="change-orders-filters">
          <select value={projectFilter} onChange={(event) => setProjectFilter(event.target.value)}>
            <option value="all">Tüm Projeler</option>
            {projects.map((project) => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
            {STATUS_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>{item.label}</option>
            ))}
          </select>
          <button type="button" className="btn-secondary" onClick={fetchChangeOrders}>
            Yenile
          </button>
        </div>

        {error && <div className="error-banner">{error}</div>}

        {loading ? (
          <div className="loading-banner">Yükleniyor...</div>
        ) : rows.length === 0 ? (
          <div className="empty-banner">Kayıt bulunamadı.</div>
        ) : (
          <div className="change-orders-table-wrap">
            <table className="change-orders-table">
              <thead>
                <tr>
                  <th>Başlık</th>
                  <th>Proje</th>
                  <th>Katman</th>
                  <th>Süre Etkisi</th>
                  <th>Maliyet Etkisi</th>
                  <th>Durum</th>
                  <th>Revizyon</th>
                  <th>İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className={Number(row.id) === focusedChangeOrderId ? 'row-focused' : ''}>
                    <td>
                      <strong>{row.title}</strong>
                      <div className="row-subtext">{row.description || '-'}</div>
                    </td>
                    <td>{row.project_name || row.project_id}</td>
                    <td>{row.board_node_name || '-'}</td>
                    <td>{Number(row.schedule_impact_days || 0)} gün</td>
                    <td>{Number(row.cost_impact || 0).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} TL</td>
                    <td>
                      <span className={`status-chip ${row.status}`}>{STATUS_LABELS[row.status] || row.status}</span>
                    </td>
                    <td>Rev.{row.revision_no || 1}</td>
                    <td>
                      <div className="row-actions">
                        {canManage && (
                          <button type="button" className="btn-ghost" onClick={() => openEditForm(row)}>
                            Düzenle
                          </button>
                        )}
                        {canManage && row.status === 'draft' && (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={approvingId === Number(row.id)}
                            onClick={() => submitForApproval(row.id)}
                          >
                            Onaya Gönder
                          </button>
                        )}
                        {canApprove && row.status === 'pending_approval' && (
                          <>
                            <button
                              type="button"
                              className="btn-ghost approve"
                              disabled={approvingId === Number(row.id)}
                              onClick={() => approveChangeOrder(row.id, 'approved')}
                            >
                              Onayla
                            </button>
                            <button
                              type="button"
                              className="btn-ghost reject"
                              disabled={approvingId === Number(row.id)}
                              onClick={() => approveChangeOrder(row.id, 'rejected')}
                            >
                              Reddet
                            </button>
                          </>
                        )}
                        {canManage && (
                          <button
                            type="button"
                            className="btn-ghost"
                            disabled={approvingId === Number(row.id)}
                            onClick={() => createRevision(row)}
                          >
                            Revizyon
                          </button>
                        )}
                        {canManage && (
                          <button type="button" className="btn-ghost danger" onClick={() => removeChangeOrder(row.id)}>
                            Sil
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-card" onClick={(event) => event.stopPropagation()}>
            <div className="modal-head">
              <h2>{editingRow ? 'Değişiklik Emri Düzenle' : 'Yeni Değişiklik Emri'}</h2>
              <button type="button" className="btn-close" onClick={closeForm}>X</button>
            </div>

            <div className="modal-grid">
              <label>
                Proje
                <select
                  value={formState.project_id}
                  onChange={(event) => setFormState((prev) => ({ ...prev, project_id: event.target.value, board_node_id: '' }))}
                >
                  <option value="">Seçiniz</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>{project.name}</option>
                  ))}
                </select>
              </label>

              <label>
                Katman / Board
                <select
                  value={formState.board_node_id}
                  onChange={(event) => setFormState((prev) => ({ ...prev, board_node_id: event.target.value }))}
                  disabled={!formState.project_id || boardLoading}
                >
                  <option value="">{boardLoading ? 'Yükleniyor...' : 'Seçiniz'}</option>
                  {boardOptions.map((item) => (
                    <option key={item.id} value={item.id}>{item.label}</option>
                  ))}
                </select>
              </label>

              <label>
                Başlık
                <input
                  type="text"
                  value={formState.title}
                  onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                />
              </label>

              <label>
                Sorumlu
                <select
                  value={formState.owner_id}
                  onChange={(event) => setFormState((prev) => ({ ...prev, owner_id: event.target.value }))}
                >
                  <option value="">Seçiniz</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.username}{user.job_title ? ` (${user.job_title})` : ''}
                    </option>
                  ))}
                </select>
              </label>

              <label>
                Süre Etkisi (gün)
                <input
                  type="number"
                  value={formState.schedule_impact_days}
                  onChange={(event) => setFormState((prev) => ({ ...prev, schedule_impact_days: event.target.value }))}
                />
              </label>

              <label>
                Maliyet Etkisi (TL)
                <input
                  type="number"
                  step="0.01"
                  value={formState.cost_impact}
                  onChange={(event) => setFormState((prev) => ({ ...prev, cost_impact: event.target.value }))}
                />
              </label>

              <label className="full-row">
                Kapsam Etkisi
                <textarea
                  rows="2"
                  value={formState.scope_impact}
                  onChange={(event) => setFormState((prev) => ({ ...prev, scope_impact: event.target.value }))}
                />
              </label>

              <label className="full-row">
                Açıklama
                <textarea
                  rows="3"
                  value={formState.description}
                  onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                />
              </label>
            </div>

            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeForm}>İptal</button>
              <button type="button" className="btn-primary" disabled={submitting} onClick={saveForm}>
                {submitting ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeOrders;


